import React from "react";

import {
	Button,
	Card,
	Divider,
	Picto,
	SummaryList,
	Title,
	TSummaryListItem,
} from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import { weeklyTotals } from "@/utils/UWorkDays";
import { cn } from "@/utils/cn";

import { TOnboardingResult } from "@/types";

import { ONBOARDING_MAX_WIDTH_CLASS } from "../COnboarding";

interface IDoneStepProps {
	result: TOnboardingResult;
	onFinish: () => void;
}

const Swatch: React.FC<{ color: string; children: React.ReactNode }> = ({
	color,
	children,
}) => (
	<span className="inline-flex items-center gap-2">
		<span
			className="inline-block h-3 w-3 shrink-0 rounded-full"
			style={{ backgroundColor: color }}
			aria-hidden
		/>
		{children}
	</span>
);

export const DoneStep: React.FC<IDoneStepProps> = ({ result, onFinish }) => {
	const { days, hours } = weeklyTotals(result.rhythm);
	const none = (
		<span className="text-[var(--amp-color-sub)]">
			{t("onboarding.done.none")}
		</span>
	);

	const items: TSummaryListItem[] = [
		{
			label: t("views.label"),
			value: result.viewName || none,
		},
		{
			label: t("onboarding.steps.rhythm.name"),
			value: t("onboarding.done.rhythm", { days, hours }),
		},
		{
			label: t("company.label"),
			value: result.companyName ?? none,
		},
		{
			label: t("onboarding.steps.project.name"),
			value: (
				<Swatch color={result.projectColor}>
					{result.projectName}
				</Swatch>
			),
		},
		{
			label: t("onboarding.steps.project.clientName"),
			value: result.clientName ?? none,
		},
	];

	return (
		<Card
			elevation={2}
			className={cn([
				"flex w-full flex-col gap-6",
				ONBOARDING_MAX_WIDTH_CLASS,
			])}
		>
			<div className="flex flex-col items-center gap-4 pt-2 text-center">
				<div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--amp-color-success-tint)] text-[var(--amp-color-success)]">
					<Picto icon="check" className="h-8 w-8" currentColor />
				</div>
				<Title as="h1" size="h2">
					{t("onboarding.done.title")}
				</Title>
				<p className="m-0 max-w-[520px] text-sm leading-[22px] text-[var(--amp-color-sub)]">
					{t("onboarding.done.intro")}
				</p>
			</div>
			<Divider />
			<SummaryList
				items={items}
				layout="grid"
				gridConfig={{
					columns: 2,
					minItemWidth: "220px",
					gap: "1rem 2rem",
				}}
			/>
			<Divider />
			<div className="flex justify-center">
				<Button picto="calendar" onClick={onFinish}>
					{t("onboarding.done.cta")}
				</Button>
			</div>
		</Card>
	);
};
